import { useAuth0 } from '@auth0/auth0-react'
import Cookies from 'js-cookie'
import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import Swal from 'sweetalert2'

import type { RootState } from '@/store'
import { apiService, useLazyClearSessionQuery } from '@/store/apiService'
import { setIsMultiSession } from '@/store/appSlice'

interface AppState {
  isMultiSession: boolean
}

const ErrorPage = () => {
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const code = searchParams.get('code')
  const errorCode = searchParams.get('code')
  const sessionId = Cookies.get('session_id')

  const { isMultiSession } = useSelector<RootState, AppState>(
    (state) => state.app,
  )

  const { getIdTokenClaims, loginWithRedirect, logout } = useAuth0()

  const [clearSession] = useLazyClearSessionQuery()
  const isHandlingError = useRef(false)
  const handleMultiSessionError = async () => {
    try {
      await getIdTokenClaims()
      await clearSession('multi', false).unwrap()

      dispatch(setIsMultiSession(false))
      dispatch(
        apiService.util.invalidateTags([{ type: 'Recipes', id: 'LIST' }]),
      )

      setTimeout(() => {
        window.location.href = '/'
      }, 200)
    } catch (error) {
      console.error('Error handling multi-session:', error)
    }
  }

  const handleAccessDenied = () => {
    Swal.fire({
      title: 'Authorization Failure: Development Build Access',
      html: `
        <div >
          <p>
            Access to the current Matisse application build is <strong>strictly limited</strong> to accounts provisioned for the Beta Program.
          </p>
          <p>
            If you are an authorized user, please verify your login credentials.
          </p>
          <p>
            Otherwise, please use the primary application URL:
            <br />
            <a href="https://app.matisse.ai" target="_blank" rel="noopener noreferrer" style="color: #3085d6; font-weight: bold; text-decoration: underline; display: inline-block; margin-top: 5px;">
              https://app.matisse.ai
            </a>
          </p>
        </div>
      `,
      icon: 'error',
      confirmButtonText: 'OK',
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result.isConfirmed) {
        Cookies.remove('session_id')

        logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        }).catch(() => {
          loginWithRedirect().catch(() => {
            window.location.href = window.location.origin
          })
        })
      }
    })
  }

  useEffect(() => {
    if (isMultiSession || code === '3005') {
      if (!isHandlingError.current) {
        isHandlingError.current = true
        handleMultiSessionError()
      }
    } else if (code === '3013') {
      handleAccessDenied()
    } else if (code === '551') {
      Swal.fire({
        title: 'We&rsquo;ll be back soon!',
        text: "Sorry for the inconvenience but we're performing some maintenance at the moment. If you need to you can always contact us at info@matisse.ai, otherwise we'll be back online shortly!",
        icon: 'warning',
        allowOutsideClick: false,
      })
    } else {
      window.location.href = '/something-went-wrong'
    }
  }, [sessionId, errorCode, clearSession, dispatch, isMultiSession, code])

  return null
}

export default ErrorPage
